import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';

// Pages chargées directement (critiques pour le premier rendu)
import LoginPage from './pages/LoginPage';
import TicketList from './pages/TicketList';

// Pages lazy-loaded (chargées à la demande)
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const KanbanBoard = lazy(() => import('./pages/KanbanBoard'));
const TicketDetail = lazy(() => import('./pages/TicketDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const Users = lazy(() => import('./pages/Users'));
const QuickReplies = lazy(() => import('./pages/QuickReplies'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const KBArticle = lazy(() => import('./pages/KBArticle'));
const PublicTicket = lazy(() => import('./pages/PublicTicket'));
const KBEditor = lazy(() => import('./pages/KBEditor'));
const AutomationSettings = lazy(() => import('./pages/AutomationSettings'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const Assets = lazy(() => import('./pages/Assets'));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'));
const Webhooks = lazy(() => import('./pages/Webhooks'));
const Surveys = lazy(() => import('./pages/Surveys'));
const SurveyPublic = lazy(() => import('./pages/SurveyPublic'));

import Layout from './components/Layout';
import { Loader2 } from 'lucide-react';

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
  </div>
);

const ProtectedRoute = ({ children, requireAgent = false, requireAdmin = false }) => {
  const { isAuthenticated, loading, isAgent, isAdmin } = useAuth();
  const { loading: tenantLoading } = useTenant();
  const location = useLocation();

  if (loading || tenantLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    // Sauvegarder l'URL d'origine pour rediriger après login
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  if (requireAgent && !isAgent) return <Navigate to="/tickets" replace />;
  if (requireAdmin && !isAdmin) return <Navigate to="/tickets" replace />;

  return (
    <Layout>
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </Layout>
  );
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { loading: tenantLoading, error: tenantError } = useTenant();
  const location = useLocation();

  if (loading || tenantLoading) {
    return <PageLoader />;
  }

  if (tenantError) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900"><div className="text-center"><h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Organisation non trouvée</h1></div></div>;
  }

  // Ne pas rediriger si on est sur /reset-password avec un token (reset de mot de passe)
  const isResetPassword = location.pathname === '/reset-password' && location.search.includes('token=');
  if (isAuthenticated && !isResetPassword) return <Navigate to="/tickets" replace />;
  return children;
};

// Redirige vers le bon dashboard selon le rôle
const HomeRedirect = () => {
  const { isAgent } = useAuth();
  return <Navigate to={isAgent ? '/dashboard' : '/my-dashboard'} replace />;
};

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/public/ticket/:token" element={<ErrorBoundary><PublicTicket /></ErrorBoundary>} />
        <Route path="/survey/:token" element={<ErrorBoundary><SurveyPublic /></ErrorBoundary>} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute requireAgent><Dashboard /></ProtectedRoute>} />
        <Route path="/my-dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
        <Route path="/tickets" element={<ProtectedRoute><TicketList /></ProtectedRoute>} />
        <Route path="/kanban" element={<ProtectedRoute requireAgent><KanbanBoard /></ProtectedRoute>} />
        <Route path="/tickets/:ticketNumber" element={<ProtectedRoute><TicketDetail /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/automation" element={<ProtectedRoute requireAdmin><AutomationSettings /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute requireAdmin><Users /></ProtectedRoute>} />
        <Route path="/quick-replies" element={<ProtectedRoute requireAgent><QuickReplies /></ProtectedRoute>} />
        <Route path="/kb" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
        <Route path="/kb/new" element={<ProtectedRoute requireAgent><KBEditor /></ProtectedRoute>} />
        <Route path="/kb/edit/:id" element={<ProtectedRoute requireAgent><KBEditor /></ProtectedRoute>} />
        <Route path="/kb/:slug" element={<ProtectedRoute><KBArticle /></ProtectedRoute>} />
        <Route path="/assets" element={<ProtectedRoute requireAgent><Assets /></ProtectedRoute>} />
        <Route path="/email-templates" element={<ProtectedRoute requireAdmin><EmailTemplates /></ProtectedRoute>} />
        <Route path="/webhooks" element={<ProtectedRoute requireAdmin><Webhooks /></ProtectedRoute>} />
        <Route path="/surveys" element={<ProtectedRoute requireAdmin><Surveys /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <TenantProvider>
          <AuthProvider>
            <SocketProvider>
              <ErrorBoundary title="Erreur critique" message="L'application a rencontré une erreur inattendue.">
                <AppRoutes />
              </ErrorBoundary>
            </SocketProvider>
          </AuthProvider>
        </TenantProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
